// Editable service content. Each entry powers the bento card, nav menu and /services/:slug page.
export const services = [
  {
    slug: "field-services",
    index: "01",
    name: "Field Services",
    short: "Engineers on the ground — from first survey to final decommission.",
    summary:
      "Skilled field engineering for the physical side of technology: understanding your sites, preparing equipment and getting it installed, tagged and accounted for.",
    image: "/images/engineer-vest.jpg",
    imageAlt: "Field engineer in a high-visibility vest beside an open electrical cabinet",
    capabilities: [
      ["Audits & site surveys", "Documenting existing infrastructure, power, cabling paths and constraints before any work begins."],
      ["Inspections", "Structured on-site checks of installed equipment, with findings recorded clearly."],
      ["Staging", "Unboxing, imaging, configuring and labelling equipment before it reaches site."],
      ["Asset tagging", "Consistent tagging and registers so every device can be traced through its lifecycle."],
      ["Logistics", "Coordinating movement of equipment between warehouses, offices and project sites."],
      ["Installation & decommissioning", "Careful deployment of new equipment and responsible removal of the old."],
    ],
    outcomes: ["Fewer surprises on installation day", "A clear record of what is where", "One accountable field partner"],
    audience: ["Construction", "Energy", "Commercial Facilities", "Education", "Multi-site organisations"],
    applications: ["Pre-installation survey of a new office or branch", "Tagging and registering IT assets across several sites", "Staging laptops and network kits before a rollout", "Safely removing and recording old equipment"],
    cta: "site_survey",
  },
  {
    slug: "digital-infrastructure",
    index: "02",
    name: "Digital Infrastructure",
    short: "The networks, wireless and AV systems your organisation runs on.",
    summary:
      "Design and deployment of the connectivity and building technology that modern workplaces depend on — planned around how your people actually use the space.",
    image: "/images/patch-panel.jpg",
    imageAlt: "Dense rows of network cables plugged into a patch panel",
    capabilities: [
      ["Enterprise Wi-Fi", "Coverage planning and deployment for offices, campuses and facilities."],
      ["Fixed wireless", "Point-to-point and point-to-multipoint links where cabling is impractical."],
      ["Structured networking", "Switching, routing, cabling and rack organisation built for growth."],
      ["AV & video conferencing", "Meeting rooms and spaces that simply work when people walk in."],
      ["CCTV & access control", "Camera and entry systems planned around your security needs."],
      ["Equipment deployment", "Rollouts of network and building technology across one or many sites."],
    ],
    outcomes: ["Dependable connectivity", "Tidy, documented infrastructure", "Systems sized for tomorrow"],
    audience: ["Commercial Facilities", "Education", "Construction", "Hospitality", "Healthcare"],
    applications: ["Wi-Fi coverage for open-plan offices or campuses", "Linking two buildings with a fixed wireless connection", "Fitting out meeting rooms with video conferencing", "Planning CCTV and access control for a new facility"],
    cta: "site_survey",
  },
  {
    slug: "hardware-software",
    index: "03",
    name: "Hardware & Software",
    short: "The right business equipment and licences, set up properly.",
    summary:
      "Sourcing, setting up and supporting the devices and software your teams need — so new equipment is productive from day one.",
    image: "/images/hardware-tech.jpg",
    imageAlt: "Technician assembling computer hardware with a screwdriver",
    capabilities: [
      ["Business hardware procurement", "Laptops, desktops, printers, servers and peripherals matched to the work."],
      ["Setup & configuration", "Devices prepared, secured and configured to your standards."],
      ["Licensing", "Guidance on and management of business software licences."],
      ["Ongoing support", "Help when hardware or software misbehaves."],
    ],
    outcomes: ["Equipment that fits the job", "Consistent device setup", "Licences kept in order"],
    audience: ["SMEs", "Schools and colleges", "NGOs", "Growing teams", "Commercial Facilities"],
    applications: ["Equipping a new team with laptops and printers", "Setting up a computer lab", "Renewing and organising business software licences", "Standardising device configuration across staff"],
    cta: "quote",
  },
  {
    slug: "artificial-intelligence",
    index: "04",
    name: "Artificial Intelligence",
    short: "Practical AI — agents, chatbots, automation and computer vision.",
    summary:
      "Applying AI where it genuinely helps: automating repetitive work, answering customers faster and turning images and data into useful signals.",
    image: "/images/developer.jpg",
    imageAlt: "Developer working on a laptop",
    capabilities: [
      ["AI agents", "Assistants that carry out defined multi-step tasks inside your workflows."],
      ["AI chatbots", "Conversational assistants for customer service and internal knowledge."],
      ["Business automation", "Removing repetitive manual steps across documents, email and systems."],
      ["Computer vision", "Extracting meaning from camera and image data for defined use cases."],
      ["Custom intelligent solutions", "Bespoke AI tooling scoped around a specific business problem."],
    ],
    outcomes: ["Time returned to your team", "Faster responses", "AI with clear human oversight"],
    audience: ["Customer-facing businesses", "Professional services", "Logistics", "Education", "Security operations"],
    applications: ["A website chatbot that answers common customer questions", "Automatically sorting and routing incoming documents or emails", "An internal assistant that searches company knowledge", "Camera-based counting or monitoring for a defined use case"],
    cta: "consultation",
  },
  {
    slug: "software-engineering",
    index: "05",
    name: "Software Engineering",
    short: "Web, mobile and enterprise software, built to last.",
    summary:
      "Designing and building software around your operations — from customer-facing apps to internal systems and the integrations that connect them.",
    image: "/images/students-it.jpg",
    imageAlt: "Software developers working at computers in a shared studio",
    capabilities: [
      ["Web applications", "Fast, accessible web platforms and portals."],
      ["Mobile applications", "Apps for customers, field teams and staff."],
      ["Enterprise systems", "Internal tools that model how your organisation works."],
      ["APIs & integrations", "Connecting the systems you already rely on."],
    ],
    outcomes: ["Software shaped to your process", "Maintainable code", "Systems that talk to each other"],
    audience: ["Startups", "SMEs", "Enterprises", "Education", "Public-facing organisations"],
    applications: ["A customer portal or booking platform", "A mobile app for field staff", "An internal system to replace spreadsheets", "Connecting payments, CRM or accounting systems"],
    cta: "consultation",
  },
  {
    slug: "managed-services",
    index: "06",
    name: "Managed Services",
    short: "Ongoing care for your IT — support, monitoring and maintenance.",
    summary:
      "Taking day-to-day IT care off your plate with support and monitoring arrangements scoped around your organisation.",
    image: "/images/server-racks.jpg",
    imageAlt: "Server racks with indicator lights in a data room",
    capabilities: [
      ["Remote IT support", "Help for your team when issues arise."],
      ["Infrastructure monitoring", "Keeping watch over agreed systems so problems are spotted early."],
      ["Preventive maintenance", "Scheduled care that reduces avoidable failures."],
      ["Business continuity", "Planning so essential operations can keep going when things go wrong."],
    ],
    outcomes: ["Predictable IT", "Issues spotted earlier", "More focus on your business"],
    audience: ["SMEs without in-house IT", "Commercial Facilities", "Schools", "Clinics", "Multi-branch businesses"],
    applications: ["Ongoing help-desk support for staff", "Monitoring of agreed network equipment and servers", "Scheduled maintenance visits", "A practical continuity plan for key systems"],
    cta: "quote",
  },
  {
    slug: "cybersecurity",
    index: "07",
    name: "Cybersecurity",
    short: "Protecting networks, endpoints and the people who use them.",
    summary:
      "Helping organisations understand their exposure and put sensible, proportionate protections in place.",
    image: "/images/cctv.jpg",
    imageAlt: "Security camera mounted on a wall",
    capabilities: [
      ["Network protection", "Firewalls, segmentation and secure remote access."],
      ["Endpoint protection", "Securing laptops, desktops and mobile devices."],
      ["Security assessments", "Reviewing your current posture and recommending improvements."],
      ["Related solutions", "Practical measures such as backups, access policies and awareness."],
    ],
    outcomes: ["A clearer view of risk", "Layered protection", "Practical next steps"],
    audience: ["SMEs", "Financial and professional services", "Healthcare", "Education", "Commercial Facilities"],
    applications: ["A security review of your network and devices", "Rolling out endpoint protection across staff laptops", "Setting up secure remote access for hybrid teams", "Backup and access-policy improvements"],
    cta: "consultation",
  },
  {
    slug: "drone-technology",
    index: "08",
    name: "Drone Technology",
    short: "Aerial perspective for inspection, mapping and surveying.",
    summary:
      "Drone hire, sales, inspections, mapping and surveying for organisations that need a view from above — delivered subject to applicable regulatory requirements, including those of the Kenya Civil Aviation Authority.",
    image: "/images/drone.jpg",
    imageAlt: "Drone hovering in the air",
    capabilities: [
      ["Drone hire", "Access to drone capability for defined projects."],
      ["Drone sales", "Guidance on selecting equipment for your use case."],
      ["Inspections", "Visual checks of structures and assets that are hard to reach."],
      ["Mapping & surveying", "Aerial imagery to support planning and progress tracking."],
    ],
    outcomes: ["Safer inspections", "Better site visibility", "Regulation-aware operations"],
    audience: ["Construction", "Energy", "Agriculture", "Real estate", "Infrastructure owners"],
    applications: ["Progress imagery of a construction site", "Visual inspection of rooftops, towers or panels", "Aerial mapping to support planning", "Advice on choosing a drone for your operations"],
    cta: "site_survey",
    note: "All drone operations are subject to applicable regulatory requirements and approvals.",
  },
];

export const getService = (slug) => services.find((s) => s.slug === slug);
